import type { Translate } from './builder/components/nodes';
import type { FlowDefinition, JsonObject } from './builder/mapper';

/**
 * The entry points a bot is built around, and the flow each one starts as.
 *
 * A keyword flow always wins; then the first-contact flow for someone new,
 * the greeting flow for a hello, the default flow for anything else. After
 * an agent resolves a conversation, the after-resolution flow runs. "No agent
 * available" is not an entry point but a port on the Hand off step.
 */
export type EntryKind = 'first_contact' | 'greeting' | 'default' | 'post_resolution';
export const ENTRY_KINDS: EntryKind[] = ['first_contact', 'greeting', 'default', 'post_resolution'];

export function triggerType(definition: unknown): string {
  if (!definition || typeof definition !== 'object') return '';
  const trigger = (definition as JsonObject).trigger;
  if (!trigger || typeof trigger !== 'object') return '';
  const tr = trigger as JsonObject;
  if (tr.type === 'always') return 'default';
  if ((tr.type === 'message' || tr.type === 'keyword') && !(Array.isArray(tr.keywords) ? tr.keywords.length : tr.keyword)) return 'default';
  return typeof tr.type === 'string' ? tr.type : '';
}

export function entryPointDefinition(kind: EntryKind, t: Translate): FlowDefinition {
  switch (kind) {
    case 'first_contact':
      return {
        version: 2,
        trigger: { type: 'first_contact' },
        nodes: [
          { id: 'welcome', type: 'send', name: t('flows.entry.template.welcomeName'), body: t('flows.entry.template.welcome') },
          { id: 'consent', type: 'contact', name: t('flows.entry.template.consentName'), add_tags: ['welcomed'] },
          { id: 'answer', type: 'llm', name: t('knowledge.assistant.answerName'), mode: 'reply', kb_scope: 'all', send_reply: true, unsure_below: 0.6, save_as: 'llm' },
          { id: 'ask_more', type: 'send', body: t('flows.entry.template.askMore') }
        ],
        edges: [
          { from: 'welcome', to: 'consent' },
          { from: 'consent', to: 'answer' },
          { from: 'answer', to: 'ask_more', branch: 'unsure' }
        ]
      };
    case 'greeting':
      return {
        version: 2,
        trigger: { type: 'greeting' },
        nodes: [
          { id: 'hello', type: 'send', name: t('flows.entry.template.returningName'), body: t('flows.entry.template.returning') }
        ],
        edges: []
      };
    case 'post_resolution':
      return {
        version: 2,
        trigger: { type: 'post_resolution' },
        nodes: [
          { id: 'rate', type: 'ask', name: t('flows.entry.template.rateName'), question: t('flows.entry.template.rate'), variable: 'rating', options: [{ id: 'yes', title: t('flows.entry.template.rateYes') }, { id: 'no', title: t('flows.entry.template.rateNo') }] },
          { id: 'tag_yes', type: 'contact', name: t('flows.entry.template.tagYesName'), add_tags: ['csat_yes'], remove_tags: ['csat_no'] },
          { id: 'tag_no', type: 'contact', name: t('flows.entry.template.tagNoName'), add_tags: ['csat_no'], remove_tags: ['csat_yes'] },
          { id: 'thanks', type: 'end', message: t('flows.entry.template.thanks') },
          { id: 'sorry', type: 'end', message: t('flows.entry.template.sorry') }
        ],
        edges: [
          { from: 'rate', to: 'tag_yes', branch: 'yes' },
          { from: 'rate', to: 'tag_no', branch: 'no' },
          { from: 'tag_yes', to: 'thanks' },
          { from: 'tag_no', to: 'sorry' }
        ]
      };
    case 'default':
    default:
      return {
        version: 2,
        trigger: { type: 'default' },
        nodes: [
          { id: 'answer', type: 'llm', name: t('knowledge.assistant.answerName'), mode: 'reply', kb_scope: 'all', send_reply: true, unsure_below: 0.6, save_as: 'llm' },
          { id: 'not_sure', type: 'send', body: t('knowledge.assistant.unsureMessage') },
          // Asked, never assumed: the owner's ruling of 14 September 2026.
          { id: 'offer', type: 'ask', name: t('knowledge.assistant.offerName'), question: t('knowledge.assistant.offer'), variable: 'wants_help', validate: 'option', options: [{ id: 'yes', title: t('knowledge.assistant.offerYes') }, { id: 'no', title: t('knowledge.assistant.offerNo') }], retry_message: t('knowledge.assistant.offerRetry') },
          { id: 'declined', type: 'end', message: t('knowledge.assistant.declined') },
          { id: 'ask_need', type: 'ask', question: t('knowledge.assistant.askNeed'), variable: 'need', validate: 'not_empty' },
          { id: 'handoff', type: 'handoff', name: t('flows.entry.template.handoffName'), always: true, message: t('flows.entry.template.handoff') },
          { id: 'no_agent', type: 'end', message: t('flows.entry.template.noAgent') }
        ],
        edges: [
          { from: 'answer', to: 'not_sure', branch: 'unsure' },
          { from: 'not_sure', to: 'offer' },
          { from: 'offer', to: 'ask_need', branch: 'yes' },
          { from: 'offer', to: 'declined', branch: 'no' },
          { from: 'ask_need', to: 'handoff' },
          { from: 'handoff', to: 'no_agent', branch: 'no_agent' }
        ]
      };
  }
}
